import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpMethods } from '@cbre/http';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { LOCALE_MAP_CLIENT_MAP, LocaleMapId } from '@maps/maps.models';
import { deepCloneObject, UserCatalogsService, UserCatalogTemplate } from '@miq/catalog';
import { EntityType } from '@shared/models/entity-enum';
import { MapCardView } from '@shared/models/map-card-view.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { AppStorageService, LOCALE_ID } from '@shared/services/app-storage.service';
import { SharedService } from '@shared/services/shared.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IgnoreHistoryService } from './ignore-history.service';
import { sanitizeGeographyInput } from './records-search.helper';
import { AuditHistoryParams, CloseEntityRecord, FetchHistoricalStatsColumns } from './records-search.service.models';
import { FilterRequest, StatsRequest } from './search-filters.model';
import {
  HistoricalStatsAuditResults,
  AuditPayload,
  AuditResults,
  MarkersSearchResults,
  SearchResults,
} from './search-results.model';
import { DataObject } from '@shared/models/data-object.model';
import { DEFAULT_KPIS_RESULT, KpisResult } from '@shared/components/kpi-summary/kpi-summary-columns.helper';
import { Property } from '@core/composers';
import { AllViews, SEARCH_VIEW_BY_ENTITY_MAPPING, SearchView, ViewTypes } from '../records-search.model';
// @deprecated Exports, but Temporary re-exporting from records-search.model.ts to do not affect other places
export { AllViews, SEARCH_VIEW_BY_ENTITY_MAPPING, SearchView, ViewTypes };

@Injectable({
  providedIn: 'root',
})
export class RecordsSearchService extends HttpMethods {
  constructor(
    http: HttpClient,
    private storage: AppStorageService,
    private userInfoResolver: UserInfoResolver,
    private ignoreHistoryService: IgnoreHistoryService,
    private sharedService: SharedService,
    private userCatalogsService: UserCatalogsService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    super(http);
  }

  private getUserLocaleId(): LocaleMapId {
    return this.storage.getItem<number>(LOCALE_ID);
  }

  /**
   * Returns the current user ID stored in local storage
   * @returns number
   */
  getUserId(): number {
    const userInfo = this.userInfoResolver.resolve();
    return userInfo && userInfo.id != null ? userInfo.id : -1;
  }

  #composersMap = {
    all: {}, // Use this in case the configuration will apply for all posible search views
    [ViewTypes.Table]: {
      [EntityType.Property]: Property,
    },
  };

  private _getComposer = (entity: EntityType, view: SearchView = ViewTypes.Table) =>
    this.#composersMap[view]?.[entity] ?? this.#composersMap.all[entity];

  /**
   * It calls searchView with either 'table' or 'id' view types
   * @param request {FilterRequest}
   * @param view {'table' | 'id'}
   */
  search(request: FilterRequest, view: SearchView = ViewTypes.Table, propagateErrors = false): Observable<any> {
    const composer = this._getComposer(request.entity as EntityType, view);
    // apply compose only when composer is defined otherwise return the results as is
    const resultsMapper =
      composer != null ? (r: SearchResults) => ({ ...r, results: (r.results ?? []).map(composer) }) : self => self;
    return this.searchView(request, view, propagateErrors).pipe(map(resultsMapper)) as Observable<any>;
  }

  searchOptimized(
    request: FilterRequest,
    view: SearchView = ViewTypes.Table,
    templateID: UserCatalogTemplate,
    propagateErrors = false
  ): Observable<any> {
    const composer = this._getComposer(request.entity as EntityType, view);
    // apply compose only when composer is defined otherwise return the results as is
    const resultsMapper =
      composer != null ? (r: SearchResults) => ({ ...r, results: (r.results ?? []).map(composer) }) : self => self;
    return this.optimizedSearchView(request, view, templateID, propagateErrors).pipe(
      map(resultsMapper)
    ) as Observable<any>;
  }

  protected optimizedSearchView(
    request: FilterRequest,
    view: AllViews = ViewTypes.Table,
    templateID: UserCatalogTemplate,
    propagateErrors = false
  ): Observable<any> {
    const generatedPayload = this.#generateSearchPayload(request);
    const ignoreHistoryFlag = `&ignoreHistory=${this.ignoreHistoryService.isFlagOn()}`;
    const search$ = this.commonSearchHub(
      `/search/lean?view=${view}${ignoreHistoryFlag}&templateID=${templateID}`,
      generatedPayload
    );
    return propagateErrors
      ? search$
      : search$.pipe(
          catchError(err => {
            this.logger.error(err);
            return of({ totalCount: 0, results: [] });
          })
        );
  }

  /**
   * It calls searchView with view value as 'map'
   * @param request {FilterRequest}
   */
  // TO DO : Instead of adding 'chMap' view use another query param as country e.g., country=ch
  searchMapMarkers(request: FilterRequest, propagateErrors = false): Observable<MarkersSearchResults> {
    const { entity, userId, geography, filters, skipDefaultFilters } = request;
    const view = LOCALE_MAP_CLIENT_MAP[this.getUserLocaleId()] === 'gaode' ? 'chMap' : 'map';
    return this.searchView({ entity, userId, geography, filters, skipDefaultFilters }, view, propagateErrors);
  }

  #generateSearchPayload(request: FilterRequest) {
    let requestFilters = request.filters;
    const typeAheadFilter = request.filters.find(filterItem => filterItem.type === 'multifield');
    if (typeAheadFilter) {
      const typeAheadValue = typeof typeAheadFilter.values === 'string' ? typeAheadFilter.values.trim() : '';
      if (typeAheadValue.length === 0) {
        requestFilters = request.filters.filter(filterItem => filterItem.type !== 'multifield');
      }
    }
    const defaultFilters = this.sharedService.getDefaultFilters(request.entity as EntityType);
    if (request?.skipDefaultFilters === false) {
      defaultFilters.skipDefaultFilters = false;
    }
    // Prioritize skipDefaultFilters from defaultFilters if present, else from request
    const skipDefaultFilters =
      typeof defaultFilters.skipDefaultFilters !== 'undefined'
        ? defaultFilters.skipDefaultFilters
        : request.skipDefaultFilters;
    return {
      ...defaultFilters,
      ...request,
      filters: requestFilters,
      userId: request.userId != null ? request.userId : this.getUserId(),
      ...(typeof skipDefaultFilters !== 'undefined' ? { skipDefaultFilters } : {}),
    };
  }

  /**
   * chMap is added if the user's country is CHINA and the language is CHINESE
   * @param request {FilterRequest}
   * @param view {'table' | 'map' | 'id' | 'chMap'}
   */
  private searchView(request: FilterRequest, view: AllViews = ViewTypes.Table, propagateErrors = false) {
    const generatedPayload = this.#generateSearchPayload(request);
    const ignoreHistoryFlag = `&ignoreHistory=${this.ignoreHistoryService.isFlagOn()}`;
    const search$ = this.commonSearchHub(`/search?view=${view}${ignoreHistoryFlag}`, generatedPayload);
    return propagateErrors
      ? search$
      : search$.pipe(
          catchError(err => {
            this.logger.error(err);
            return of({ totalCount: 0, results: [] });
          })
        );
  }

  /**
   * Returns option count for filter options
   * @param request {FilterRequest}
   */
  getAggregations(request: FilterRequest, view?: ViewTypes, propagateErrors = false) {
    const viewP = view ? `?view=${view}` : '';
    const lookupRequest = deepCloneObject(request) as FilterRequest;
    if (lookupRequest.after) {
      // Remove after property from request to avoid sending it to the backend
      // as it is not supported by /search/lookup endpoint
      delete lookupRequest.after;
    }
    const lookup$ = this.commonSearchHub(`/search/lookup${viewP}`, lookupRequest);
    return propagateErrors
      ? lookup$
      : (lookup$.pipe(
          catchError(err => {
            this.logger.error(err);
            return of({ totalCount: 0, aggregation: [] });
          })
        ) as Observable<any>);
  }

  getTableKPIs(request: FilterRequest) {
    const generatedPayload = this.#generateSearchPayload(request);
    return this.commonSearchHub('/search/kpi', generatedPayload).pipe(
      catchError(err => {
        this.logger.error(err);
        return of(DEFAULT_KPIS_RESULT);
      })
    ) as Observable<KpisResult>;
  }

  /**
   * Common interface for aggregations (/search/lookup) and searches (/search?view=x) endpoints
   */
  private commonSearchHub(url: string, request: FilterRequest): Observable<any> {
    const { entity, userId, filters } = request;
    let { geography, offset } = request;
    geography = geography ? sanitizeGeographyInput(geography) : null;
    if (offset != null) {
      /* MIQ-9452 ensure offset is positive integer value to prevent/correct
       * edge cases found in MIQ-9452 sending floating and/or negative offset values */
      offset = Math.floor(Math.abs(request.offset));
    }
    return this.httpPost(url, {
      ...request,
      entity,
      userId,
      geography,
      filters,
      offset,
    }) as Observable<MarkersSearchResults>;
  }

  public simpleSearch(url: string, request: FilterRequest): Observable<any> {
    return this.httpPost(url, request) as Observable<MarkersSearchResults>;
  }

  getStats(request: StatsRequest, newVersion = false): Observable<any> {
    const version = newVersion ? '/v2' : '';
    return this.httpPost(`${version}/stats/bulk`, request) as Observable<any>;
  }

  saveStats(data, newVersion = false): Observable<void> {
    const version = newVersion ? '/v2' : '';
    return this.httpPut(`${version}/stats/bulk`, data) as Observable<any>;
  }

  deleteStatRow(recordId: number): Observable<void> {
    return this.httpDelete(`/stats/quarters/${recordId}`);
  }

  saveNewStatsRecord(data, newVersion = false): Observable<void> {
    const version = newVersion ? '/v2' : '';
    return this.httpPost(`${version}/stats/bulk/new`, data) as Observable<any>;
  }

  getHistoricalStatsAudits(auditParamsReq): Observable<{ totalCount: number; results: HistoricalStatsAuditResults[] }> {
    const baseURL = '/audit/search/stats';
    const params = new HttpParams({ fromObject: auditParamsReq });

    return this.httpGet(baseURL, params) as Observable<{ totalCount: number; results: HistoricalStatsAuditResults[] }>;
  }

  getHistoricalStatsColumns(): Observable<FetchHistoricalStatsColumns> {
    // we are using v2 endpoint to get all columns
    return this.httpGet(`/v2/stats/columns`);
  }

  searchMapCardView(request: MapCardView): Observable<SearchResults> {
    return this.sharedService.searchMapCardView(request);
  }

  getFavoriteColumns(userId, entity) {
    return this.userCatalogsService.getUserColumns(userId, entity, UserCatalogTemplate.CustomDetail);
  }

  updateFavoriteColumns(userId, entity, columns) {
    return this.userCatalogsService.putMoveColumns(userId, entity, UserCatalogTemplate.CustomDetail, columns);
  }

  closeEntityRecord(
    id: number,
    entity: string,
    code: string,
    additionalData: {
      date?: Date;
      lastAvailStatusHistoryId?: number;
      lastStatusChangeEndDate?: string;
      userIntent: string;
    }
  ) {
    let params: CloseEntityRecord = {
      id,
    };
    let path: string;

    if (entity === EntityType.Availability) {
      params = {
        ...params,
        code,
        date: additionalData.date,
        last_availability_status_history_id: additionalData.lastAvailStatusHistoryId,
        last_status_change_end_date: additionalData.lastStatusChangeEndDate,
      };
      path = 'offmarket';
    } else {
      params = { ...params, name: code, date: additionalData.date };
      path = 'timclosure';
    }
    const requestUrl = additionalData.userIntent ? '&userIntent=' + additionalData.userIntent : '';
    return this.httpPut(`/write/${path}?effSourceEntity=${entity}` + requestUrl, params);
  }

  getAuditHistory(params: AuditHistoryParams): Observable<{ totalCount: number; results: AuditResults[] }> {
    const { id, entity, type, sortField, sortFieldType, sortOrder, limit, offset, status } = params;
    const auditHistoryParams = `id=${id}&entity=${entity}&type=${type}`;
    const paginationParams = `limit=${limit ?? ''}&offset=${offset ?? ''}`;
    const sortParams = `sortField=${sortField || ''}&sortFieldType=${sortFieldType || ''}&sortOrder=${sortOrder || ''}`;
    const statusParam = status != null && status != '' ? `&status=${status}` : '';
    return this.httpGet(
      `/audit/search/review?${auditHistoryParams}&${paginationParams}&${sortParams}${statusParam}`
    ) as Observable<{
      totalCount: number;
      results: AuditResults[];
    }>;
  }

  sendAuditHistory(auditInfo: AuditPayload) {
    return this.httpPost(`/audit/search/review`, auditInfo);
  }

  getSourceLineageByEntityAndId(entity: EntityType, id: number): Observable<any> {
    return this.httpGet(`/search/${entity}/${id}/sourcelineage`);
  }

  sendVerifiedEntity(entity: string, id: number) {
    return this.httpPut('/write/verify', { entity, id });
  }

  addLocation(entity: string, id: number, method: string, requestBody) {
    return this.httpPut(`/write?entity=${entity}&id=${id}&method=${method}`, requestBody);
  }

  /**
   * get top 3 occupier info and tenant mix info
   */
  getTenancyAttr(query: any): Observable<any> {
    return this.httpPost('/search?view=topOccupiers', query) as Observable<any>;
  }

  getJobsStatus(): Observable<DataObject> {
    const uri = '/search/stats/jobstatus';
    return this.httpPost(uri, {}) as Observable<DataObject>;
  }
}
